import { useState, useEffect } from 'react';
import { Check, ChevronDown, Building2 } from 'lucide-react';

function DepartmentFilter({ selectedDepartments = [], onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Available departments - simplified to IT, Admin, and Others
  const availableDepartments = [
    { id: 'IT', name: 'IT', color: '#3b82f6' },
    { id: 'Admin', name: 'Admin', color: '#10b981' },
    { id: 'Others', name: 'Others', color: '#f59e0b' }
  ];

  useEffect(() => {
    // Simulate API call to fetch departments
    setTimeout(() => {
      setDepartments(availableDepartments);
      setLoading(false);
    }, 500);
  }, []);

  const handleDepartmentToggle = (departmentId) => {
    const newSelection = selectedDepartments.includes(departmentId)
      ? selectedDepartments.filter(id => id !== departmentId)
      : [...selectedDepartments, departmentId];
    
    onChange(newSelection);
  };

  const selectAll = () => {
    onChange(departments.map(dept => dept.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getSelectedDepartmentNames = () => {
    return departments
      .filter(dept => selectedDepartments.includes(dept.id))
      .map(dept => dept.name);
  };

  const selectedNames = getSelectedDepartmentNames();
  const hasSelection = selectedDepartments.length > 0;

  if (loading) {
    return (
      <div 
        className="p-3 rounded-lg border animate-pulse"
        style={{ 
          background: 'var(--bg)',
          borderColor: 'var(--border)'
        }}
      >
        <div 
          className="h-4 rounded"
          style={{ background: 'var(--border)' }}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Department Selection Display */}
      <div 
        className="flex items-center justify-between p-3 rounded-lg border cursor-pointer"
        style={{ 
          background: hasSelection ? 'rgba(139, 69, 19, 0.05)' : 'var(--bg)',
          borderColor: hasSelection ? 'var(--primary)' : 'var(--border)'
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary)' }} />
          <span className="text-sm truncate" style={{ color: 'var(--text)' }}>
            {hasSelection 
              ? selectedNames.length === 1 
                ? selectedNames[0]
                : `${selectedNames.length} departments selected`
              : 'Select departments'
            }
          </span>
        </div>
        
        <ChevronDown 
          className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--muted)' }}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 p-2 rounded-lg border shadow-lg z-10 max-h-64 overflow-y-auto"
          style={{ 
            background: 'var(--panel)',
            borderColor: 'var(--border)'
          }}
        >
          {/* Header Actions */}
          <div className="flex justify-between items-center p-2 border-b mb-2" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
              Select Departments
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs px-2 py-1 rounded"
                style={{ 
                  background: 'var(--primary)',
                  color: 'white'
                }}
              >
                All
              </button>
              <button
                onClick={clearAll}
                className="text-xs px-2 py-1 rounded border"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Department Options */}
          <div className="space-y-1">
            {departments.map((department) => {
              const isSelected = selectedDepartments.includes(department.id);
              
              return (
                <div
                  key={department.id}
                  className="flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-opacity-50"
                  style={{ 
                    background: isSelected ? 'rgba(139, 69, 19, 0.1)' : 'transparent'
                  }}
                  onClick={() => handleDepartmentToggle(department.id)}
                >
                  {/* Checkbox */}
                  <div 
                    className="w-4 h-4 rounded border-2 flex items-center justify-center"
                    style={{ 
                      borderColor: isSelected ? department.color : 'var(--border)',
                      background: isSelected ? department.color : 'transparent'
                    }}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>

                  {/* Department Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ background: department.color }}
                      />
                      <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                        {department.name}
                      </span>
                    </div>
                    <div className="text-xs" style={{ color: 'var(--muted)' }}>
                      {department.id}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-2 pt-2 border-t text-center" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => setIsOpen(false)}
              className="text-xs px-3 py-1 rounded"
              style={{ 
                background: 'var(--border)',
                color: 'var(--text)'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentFilter;